import { supabase } from "@/integrations/supabase/client";
import { logger } from "./logger";

export interface AnalyticsUserContext {
  id?: string;
  email?: string;
  role?: string;
}

const appVersion = import.meta.env.VITE_APP_VERSION ?? "0.0.0";
type TrackEventHandler = (eventName: string, properties?: Record<string, any>) => void;

const withStructuredPayload = (
  metadata: Record<string, any> = {},
  userContext?: AnalyticsUserContext
) => ({
  ...metadata,
  timestamp: new Date().toISOString(),
  version: appVersion,
  user: {
    id: userContext?.id ?? "anonymous",
    email: userContext?.email,
    role: userContext?.role,
  },
});

// Track user actions for analytics
const defaultTrackEventHandler: TrackEventHandler = (eventName, properties) => {
  logger.info(`Analytics Event: ${eventName}`, properties);

  // In production, you can send to analytics services like:
  // - Google Analytics
  // - Mixpanel
  // - Amplitude
  // - PostHog

  if (import.meta.env.DEV) {
    console.log("📈 Track Event:", eventName, properties);
  }
};

let trackEventHandler: TrackEventHandler = defaultTrackEventHandler;

export const setTrackEventHandler = (handler: TrackEventHandler) => {
  trackEventHandler = handler;
};

export const resetTrackEventHandler = () => {
  trackEventHandler = defaultTrackEventHandler;
};

export const trackEvent: TrackEventHandler = (
  eventName: string,
  properties?: Record<string, any>
) => {
  trackEventHandler(eventName, properties);
};

// Meeting-specific analytics
export const trackMeetingCreated = (meetingId: string, title: string, waitingRoom: boolean) => {
  trackEvent("meeting_created", {
    meetingId,
    title,
    waitingRoom,
    timestamp: new Date().toISOString(),
  });
};

export const trackMeetingJoined = (meetingId: string, isHost: boolean, isGuest: boolean) => {
  trackEvent("meeting_joined", {
    meetingId,
    isHost,
    isGuest,
    timestamp: new Date().toISOString(),
  });
};

export const trackMeetingLeft = (meetingId: string, duration: number) => {
  trackEvent("meeting_left", {
    meetingId,
    duration,
    timestamp: new Date().toISOString(),
  });
};

export const trackChatMessage = (meetingId: string, messageLength: number) => {
  trackEvent("chat_message_sent", {
    meetingId,
    messageLength,
    timestamp: new Date().toISOString(),
  });
};

export const trackScreenShare = (meetingId: string, action: "started" | "stopped") => {
  trackEvent("screen_share", {
    meetingId,
    action,
    timestamp: new Date().toISOString(),
  });
};

export const trackRecording = (meetingId: string, action: "started" | "stopped") => {
  trackEvent("recording", {
    meetingId,
    action,
    timestamp: new Date().toISOString(),
  });
};

// User engagement analytics
export const trackFeatureUsage = (featureName: string, metadata?: Record<string, any>) => {
  trackEvent("feature_used", {
    feature: featureName,
    ...metadata,
    timestamp: new Date().toISOString(),
  });
};

export const trackOfflineBannerShown = (
  state: { visible: boolean; collapsed?: boolean },
  userContext?: AnalyticsUserContext
) => {
  trackEvent(
    "offline_banner_shown",
    withStructuredPayload(
      {
        visible: state.visible,
        collapsed: state.collapsed ?? false,
      },
      userContext
    )
  );
};

export const trackOfflineBannerToggled = (
  isCollapsed: boolean,
  userContext?: AnalyticsUserContext
) => {
  trackEvent(
    "offline_banner_toggled",
    withStructuredPayload(
      {
        collapsed: isCollapsed,
      },
      userContext
    )
  );
};

export const trackShortcutUsed = (
  action: string,
  metadata?: Record<string, any>,
  userContext?: AnalyticsUserContext
) => {
  trackEvent(
    "keyboard_shortcut_used",
    withStructuredPayload(
      {
        action,
        ...metadata,
      },
      userContext
    )
  );
};

export const trackModalEvent = (
  modalName: string,
  action: "open" | "close",
  metadata?: Record<string, any>,
  userContext?: AnalyticsUserContext
) => {
  trackEvent(
    "modal_interaction",
    withStructuredPayload(
      {
        modalName,
        action,
        ...metadata,
      },
      userContext
    )
  );
};

export const trackWhatsNewInteraction = (
  action: string,
  metadata?: Record<string, any>,
  userContext?: AnalyticsUserContext
) => {
  trackEvent(
    "whats_new_interaction",
    withStructuredPayload(
      {
        action,
        ...metadata,
      },
      userContext
    )
  );
};

// Error analytics
export const trackError = (errorType: string, errorMessage: string, context?: Record<string, any>) => {
  trackEvent("error_occurred", {
    errorType,
    errorMessage,
    ...context,
    timestamp: new Date().toISOString(),
  });
};

// WebRTC connection analytics
export const trackConnectionQuality = (
  meetingId: string,
  quality: "good" | "fair" | "poor",
  metrics?: Record<string, any>
) => {
  trackEvent("connection_quality", {
    meetingId,
    quality,
    ...metrics,
    timestamp: new Date().toISOString(),
  });
};

// Save analytics data to database
export const saveAnalyticsEvent = async (
  eventName: string,
  eventData: Record<string, any>
) => {
  try {
    // You can create an analytics_events table to store custom events
    // For now, we'll just log them
    logger.debug("Saving analytics event", { eventName, eventData });
    
    // Example of how to save to a custom table:
    /*
    const { error } = await supabase
      .from("analytics_events")
      .insert({
        event_name: eventName,
        event_data: eventData,
        user_id: (await supabase.auth.getUser()).data.user?.id,
        created_at: new Date().toISOString(),
      });
      
    if (error) throw error;
    */
  } catch (error) {
    logger.error("Failed to save analytics event", error as Error, { eventName });
  }
};

// Page view tracking
export const trackPageView = (pageName: string, path: string) => {
  trackEvent("page_view", {
    page: pageName,
    path,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
  });
};

// Session tracking
const sessionStartTime = Date.now();

export const trackSessionDuration = () => {
  const duration = Date.now() - sessionStartTime;
  trackEvent("session_duration", {
    duration,
    timestamp: new Date().toISOString(),
  });
};

// Track on page unload
window.addEventListener("beforeunload", () => {
  trackSessionDuration();
});
